import noop from "lodash/noop";
import React from "react";

import Grid from "veneer/Grid";
import { IconButton } from "veneer/IconButton";
import { CloseIcon } from "veneer/SvgIcon";
import TextInput from "veneer/TextInput";

import { FormInput } from "./FormInput";
import ParamFormUtils from "./utils";

interface CustomParamValue {
  errors?: string[];
  hasOverride: boolean;
  isDuplicate: boolean;
  isLegacy: boolean;
  isOverriding: boolean;
  k: string;
  v: unknown;
}

interface CustomParamFieldProps {
  field: {
    value: CustomParamValue;
  };
  handleChange: (value: CustomParamValue) => void;
  hasDuplicate: (key: string) => boolean;
  legacyParams?: Set<string>;
  onRemove?: () => void;
  readOnly?: boolean;
}

const CustomParamField = ({
  field,
  handleChange,
  hasDuplicate,
  legacyParams = new Set(),
  onRemove = noop,
  readOnly = false,
}: CustomParamFieldProps) => {
  const { k, v, hasOverride, isOverriding } = field.value;

  const onKeyChange = (nextKey: string | null): void => {
    const key = nextKey ?? "";
    const nextValue = {
      ...field.value,
      k: key,
      isLegacy: legacyParams.has(key),
      isDuplicate: hasDuplicate(key),
    };

    nextValue.errors = ParamFormUtils.getErrorMsg(nextValue);
    handleChange(nextValue);
  };

  const onValChange = (nextValue: string | null): void => {
    handleChange({ ...field.value, v: nextValue ?? "" });
  };

  const removeBtn = (
    <IconButton
      color="error"
      dataTestId="Remove.Button"
      disabled={readOnly}
      icon={<CloseIcon size="small" />}
      onClick={onRemove}
      size="small"
      title="Click to unset this parameter."
    />
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={4}>
        <TextInput
          dataTestId={`${k}.KeyInput`}
          disabled={readOnly || hasOverride}
          fullWidth
          onChange={onKeyChange}
          type="text"
          value={k}
        />
      </Grid>
      <Grid item xs={8}>
        <FormInput
          hasOverride={hasOverride}
          inputButton={removeBtn}
          isRequired={false}
        >
          <TextInput
            dataTestId={`${k}.ValueInput`}
            disabled={readOnly || (hasOverride && !isOverriding)}
            fullWidth
            onChange={onValChange}
            type="text"
            value={String(v ?? "")}
          />
        </FormInput>
      </Grid>
    </Grid>
  );
};

export { CustomParamField };
